import { useChart } from '@chakra-ui/charts';
import { For, Text, useToken, VStack } from '@chakra-ui/react';
import ChartWrapper from '@components/ui/chart/ChartWrapper';
import { Tooltip as CustomTooltip } from '@components/ui/tooltip';
import { targetColor } from '@utils/map/map.constants';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
  YAxisTickContentProps,
} from 'recharts';

import { IdDroneCountType } from '@/types/data/statistic.types';
import { ClassificationEnum } from '@/types/sensor/classification.types';

type Props = {
  data: IdDroneCountType[];
};

const CustomGraphTooltip = (props: TooltipContentProps) => {
  const { active, payload, label } = props;
  const isVisible = active && payload.length !== 0;
  if (!isVisible) {
    return;
  }
  return (
    <VStack gap={1} paddingY={1} paddingX={1.5} backgroundColor="neutral.black">
      <Text alignSelf="start">{label}</Text>
      <VStack gap={0.25} width="100%" alignItems="start">
        <For each={payload}>
          {(entry) => (
            <Text key={entry.dataKey as string}>
              <FormattedMessage
                id="components.notificationSearch.reporting.values"
                values={{ name: entry.name, count: entry.value }}
              />
            </Text>
          )}
        </For>
      </VStack>
    </VStack>
  );
};

const CustomTickLabel = (props: YAxisTickContentProps) => {
  const { x, y, payload } = props;

  const maxLength = 20;
  const isOverflowing = payload.value.length > maxLength;

  const display = isOverflowing ? `${payload.value.slice(0, maxLength)}…` : payload.value;

  return (
    <foreignObject x={Number(x) - 142} y={Number(y) - 8} width={145} height={30}>
      <CustomTooltip content={payload.value} disabled={!isOverflowing}>
        <Text
          fontSize="12px"
          color="neutral.300"
          truncate
          textAlign="right"
          cursor="pointer"
          onClick={() => navigator.clipboard.writeText(payload.value)}
        >
          {display}
        </Text>
      </CustomTooltip>
    </foreignObject>
  );
};

export default function DroneOccurrenceGraph({ data }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [neutral300, neutral700] = useToken('colors', ['neutral.300', 'neutral.700']);

  const chart = useChart({
    data: data.map((entry) => ({
      id: entry.id,
      countByClassification: entry.countByClassification.reduce(
        (acc, curr) => ({ ...acc, [curr.classification]: curr.count }),
        {},
      ),
    })),
  });

  return (
    <ChartWrapper
      width="546px"
      isEmpty={data.length === 0}
      emptyLabel={formatMessage({ id: 'components.notificationSearch.reporting.noDetection' })}
      title={formatMessage({ id: 'components.notificationSearch.reporting.droneOccurence' })}
    >
      <BarChart style={{ width: '100%', height: '100%' }} layout="vertical" data={chart.data} responsive barSize={16}>
        <CartesianGrid strokeWidth={2} stroke={neutral700} horizontal={false} />
        <XAxis
          axisLine={false}
          tickLine={false}
          type="number"
          domain={[0, 'dataMax']}
          niceTicks="adaptive"
          tick={{
            fill: neutral300,
          }}
        />
        <Tooltip
          cursor={{ fill: neutral700 }}
          content={CustomGraphTooltip}
          isAnimationActive={true}
          itemSorter={() => 0}
        />
        <YAxis axisLine={false} tickLine={false} dataKey="id" type="category" width={145} tick={CustomTickLabel} />
        <For each={Object.values(ClassificationEnum).toReversed()}>
          {(classification) => (
            <Bar
              key={classification}
              dataKey={`countByClassification.${classification}`}
              name={formatMessage({ id: `target.classification.${classification}` })}
              stackId="total"
              fill={targetColor[classification]}
              activeBar={false}
            />
          )}
        </For>
      </BarChart>
    </ChartWrapper>
  );
}
